import { useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Download, ShieldAlert } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { BarreFiltres } from '../components/ui/BarreFiltres';
import { ChampDate } from '../components/ui/ChampDate';
import { ChampFiltre, ChampRecherche, Onglets, PanneauOnglet } from '../components/ui/Onglets';
import { Modale } from '../components/ui/Modale';
import { useToast } from '../components/ui/useToast';
import { usePerimetre } from '../site/usePerimetre';
import { ErreurApi } from '../lib/api';
import { useTaillePage } from '../lib/useTaillePage';
import { useValeurDifferee } from '../lib/useRecherche';
import { euros, heures, jourCourt } from '../lib/useFormateurs';
import type { ChoixPeriode } from '../lib/useDashboardDirection';
import {
  exporterVueGlobale,
  useApprenantsGlobale,
  useFormateursGlobale,
  useFormationsGlobale,
  type DateLue,
  type LigneApprenantGlobale,
  type LigneFormateurGlobale,
  type LigneFormationGlobale,
  type ListeVueGlobale,
  type PeriodeVueGlobale,
} from '../lib/useVueGlobale';

type Filtres = Record<string, string | number | boolean | undefined>;

const LISTES: { cle: ListeVueGlobale; libelle: string }[] = [
  { cle: 'apprenants', libelle: 'Apprenants' },
  { cle: 'formateurs', libelle: 'Formateurs' },
  { cle: 'formations', libelle: 'Formations' },
];

function listeDe(valeur: string | null): ListeVueGlobale {
  return LISTES.some((liste) => liste.cle === valeur) ? (valeur as ListeVueGlobale) : 'apprenants';
}

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

function taux(valeur: number | null): string {
  return valeur === null ? '—' : `${String(valeur).replace('.', ',')} %`;
}

function dureeHeures(minutes: number): string {
  return heures(Math.round((minutes / 60) * 100) / 100);
}

function pluriel(nombre: number, singulier: string, plurielTexte = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielTexte : singulier}`;
}

/**
 * Vue globale de la direction (phase 5, TF-426) : apprenants, formateurs et
 * formations de l'organisme en listes consolidées — recherche, filtres, accès
 * à la fiche de chacun et export Excel de la liste filtrée.
 *
 * L'écran ne compte rien : chaque colonne vient de `/api/vue-globale/…`, qui
 * relit les règles des écrans des pôles. La liste montre une synthèse ; le
 * détail est dans la fiche — dossier apprenant, fiche du formateur dans
 * « Formateurs », programme dans « Formations & programmes ».
 */
export function DirVueGlobalePage(): JSX.Element {
  const [parametres, setParametres] = useSearchParams();
  const liste = listeDe(parametres.get('onglet'));
  const { site } = usePerimetre();

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Vue globale"
        sousTitre="Apprenants, formateurs et formations · listes consolidées, fiches détaillées et export Excel"
        actions={<BarreSites />}
      />
      <Onglets
        onglets={LISTES}
        actif={liste}
        onChanger={(cle) => setParametres({ onglet: cle }, { replace: true })}
        etiquette="Listes de la vue globale"
        idPanneau="panneau-vue-globale"
      />
      <PanneauOnglet id="panneau-vue-globale" actif={liste} className="space-y-6">
        {liste === 'apprenants' && <VueApprenants site={site} />}
        {liste === 'formateurs' && <VueFormateurs site={site} />}
        {liste === 'formations' && <VueFormations site={site} />}
      </PanneauOnglet>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Briques communes
// ---------------------------------------------------------------------------

/** Bascule d'un filtre oui/non, comme « Mineur » et « RQTH » dans « Apprenants ». */
function FiltreBascule({
  libelle,
  actif,
  onBasculer,
}: {
  libelle: string;
  actif: boolean;
  onBasculer: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onBasculer}
      aria-pressed={actif}
      className={`min-h-[44px] rounded-lg px-3 text-xs font-semibold transition ${
        actif
          ? 'bg-codialis-navy text-white'
          : 'border border-gray-200 bg-white text-codialis-gray hover:bg-gray-50'
      }`}
    >
      {libelle}
    </button>
  );
}

interface FiltrePeriode {
  periode: ChoixPeriode;
  du: string;
  au: string;
}

const PERIODE_PAR_DEFAUT: FiltrePeriode = { periode: 'MOIS', du: '', au: '' };

/** Période des colonnes d'activité, comme sur le tableau de bord : le mois en cours par défaut. */
function ChoixDePeriode({
  filtre,
  options,
  affichee,
  onChanger,
}: {
  filtre: FiltrePeriode;
  options: { valeur: ChoixPeriode; libelle: string }[] | undefined;
  affichee: PeriodeVueGlobale | undefined;
  onChanger: (filtre: FiltrePeriode) => void;
}): JSX.Element {
  return (
    <>
      <ChampFiltre
        valeur={filtre.periode}
        onChange={(valeur) => {
          const periode = valeur as ChoixPeriode;
          // Des dates libres partent de la période affichée.
          onChanger({
            periode,
            du: periode === 'LIBRE' && !filtre.du ? (affichee?.du ?? '') : filtre.du,
            au: periode === 'LIBRE' && !filtre.au ? (affichee?.au ?? '') : filtre.au,
          });
        }}
        etiquette="Période des colonnes d’activité"
      >
        {(options ?? [{ valeur: 'MOIS' as ChoixPeriode, libelle: 'Mois en cours' }]).map((option) => (
          <option key={option.valeur} value={option.valeur}>
            {option.libelle}
          </option>
        ))}
      </ChampFiltre>
      {filtre.periode === 'LIBRE' && (
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <ChampDate
            libelle="Du"
            valeur={filtre.du}
            max={filtre.au || undefined}
            onChange={(du) => onChanger({ ...filtre, du })}
          />
          <ChampDate
            libelle="au"
            valeur={filtre.au}
            min={filtre.du || undefined}
            onChange={(au) => onChanger({ ...filtre, au })}
          />
        </div>
      )}
    </>
  );
}

function EtatChargement({
  isLoading,
  error,
  vide,
  libelle,
  messageVide,
}: {
  isLoading: boolean;
  error: unknown;
  vide: boolean;
  libelle: string;
  messageVide: string;
}): JSX.Element | null {
  if (isLoading) return <Chargement libelle={libelle} />;
  if (error) return <MessageErreur message={messageErreur(error, 'La liste n’a pas pu être chargée.')} />;
  if (vide) {
    return <p className="rounded-2xl bg-white px-4 py-12 text-center text-sm text-codialis-gray shadow-card">{messageVide}</p>;
  }
  return null;
}

/** Export d'une liste qui ne porte pas de donnée sensible : formateurs, formations. */
function useExportSimple(liste: ListeVueGlobale, filtres: Filtres) {
  const toast = useToast();
  const [enCours, setEnCours] = useState(false);
  const exporter = async (): Promise<void> => {
    setEnCours(true);
    try {
      const { lignes } = await exporterVueGlobale(liste, filtres);
      toast(`Fichier exporté — ${pluriel(lignes, 'ligne')}`);
    } catch (erreur) {
      toast(messageErreur(erreur, 'L’export n’a pas pu être produit.'));
    } finally {
      setEnCours(false);
    }
  };
  return { enCours, exporter };
}

const CLASSE_TH = 'px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray xl:px-4';
const CLASSE_TD = 'px-3 py-3 align-top xl:px-4';
const CLASSE_LIEN_FICHE =
  'inline-flex min-h-[36px] items-center whitespace-nowrap text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]';

function Vignette({ initiales }: { initiales: string }): JSX.Element {
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-codialis-sky to-codialis-blue text-xs font-bold text-white">
      {initiales}
    </span>
  );
}

function Detail({ children }: { children: ReactNode }): JSX.Element {
  return <div className="text-[11px] text-codialis-gray">{children}</div>;
}

// ---------------------------------------------------------------------------
// Apprenants
// ---------------------------------------------------------------------------

interface FiltresApprenants {
  recherche: string;
  formation: string;
  promotion: string;
  mode: string;
  statut: string;
  situation: string;
  contrat: string;
  referent: string;
  mineur: boolean;
  rqth: boolean;
  seuil: boolean;
}

const SANS_FILTRE_APPRENANTS: FiltresApprenants = {
  recherche: '',
  formation: '',
  promotion: '',
  mode: '',
  statut: '',
  situation: '',
  contrat: '',
  referent: '',
  mineur: false,
  rqth: false,
  seuil: false,
};

const ETATS_CONTRAT: { valeur: string; libelle: string }[] = [
  { valeur: 'ACTIF', libelle: 'Contrat actif' },
  { valeur: 'VALIDE', libelle: 'Contrat validé' },
  { valeur: 'A_VALIDER', libelle: 'Contrat à valider' },
  { valeur: 'ROMPU', libelle: 'Contrat rompu' },
  { valeur: 'TERMINE', libelle: 'Contrat terminé' },
  { valeur: 'SANS_CONTRAT', libelle: 'Sans contrat' },
];

function VueApprenants({ site }: { site: string }): JSX.Element {
  const [filtres, setFiltres] = useState<FiltresApprenants>(SANS_FILTRE_APPRENANTS);
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  const [exportOuvert, setExportOuvert] = useState(false);
  const recherche = useValeurDifferee(filtres.recherche.trim(), 300);

  const parametres: Filtres = { site, ...filtres, recherche, page, taille: taillePage };
  const { data, isLoading, error } = useApprenantsGlobale(parametres);

  // Un filtre, une antenne ou une taille de page qui change ramène à la première page.
  useEffect(() => setPage(1), [site, taillePage, recherche, filtres.formation, filtres.promotion, filtres.mode, filtres.statut, filtres.situation, filtres.contrat, filtres.referent, filtres.mineur, filtres.rqth, filtres.seuil]);

  const changer = <C extends keyof FiltresApprenants>(cle: C, valeur: FiltresApprenants[C]): void =>
    setFiltres((avant) => ({ ...avant, [cle]: valeur }));
  const filtreActif = JSON.stringify(filtres) !== JSON.stringify(SANS_FILTRE_APPRENANTS);
  const lignes = data?.apprenants ?? [];
  const total = data?.total ?? 0;
  const compteurs = data?.compteurs;
  const promotions = data?.options.promotions ?? [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Compteur libelle="Dossiers" valeur={compteurs?.dossiers ?? '—'} detail="Périmètre de l’antenne choisie" />
        <Compteur libelle="En formation" valeur={compteurs?.enFormation ?? '—'} ton="mint" detail="Apprenants actifs aujourd’hui" />
        <Compteur libelle="Sous contrat" valeur={compteurs?.sousContrat ?? '—'} ton="blue" detail="Contrat validé ou actif" />
        <Compteur
          libelle="Sans formateur référent"
          valeur={compteurs?.sansReferent ?? '—'}
          ton={compteurs && compteurs.sansReferent > 0 ? 'warm' : 'navy'}
          detail="Parmi les apprenants en formation"
        />
        <Compteur
          libelle="Seuil d’absences"
          valeur={compteurs?.seuilAtteint ?? '—'}
          ton={compteurs && compteurs.seuilAtteint > 0 ? 'red' : 'navy'}
          detail="Atteint, absences définitives non justifiées"
        />
      </div>

      <BarreFiltres
        onReinitialiser={filtreActif ? () => setFiltres(SANS_FILTRE_APPRENANTS) : null}
        onExporter={() => setExportOuvert(true)}
        exportEnCours={false}
      >
        <ChampRecherche
          valeur={filtres.recherche}
          onChange={(valeur) => changer('recherche', valeur)}
          etiquette="Rechercher un apprenant"
          placeholder="Nom, prénom ou e-mail…"
        />
        <ChampFiltre valeur={filtres.formation} onChange={(valeur) => setFiltres((avant) => ({ ...avant, formation: valeur, promotion: '' }))} etiquette="Formation">
          <option value="">Toutes les formations</option>
          {data?.options.formations.map((formation) => (
            <option key={formation.code} value={formation.code}>
              {formation.titre}
            </option>
          ))}
          <option value="SANS">Sans formation rattachée</option>
        </ChampFiltre>
        <ChampFiltre valeur={filtres.promotion} onChange={(valeur) => changer('promotion', valeur)} etiquette="Promotion">
          <option value="">Toutes les promotions</option>
          {promotions.map((promotion) => (
            <option key={promotion.cle} value={promotion.cle}>
              {promotion.libelle} · {promotion.siteCode}
            </option>
          ))}
        </ChampFiltre>
        <ChampFiltre valeur={filtres.statut} onChange={(valeur) => changer('statut', valeur)} etiquette="Statut du dossier">
          <option value="">Tous les statuts</option>
          {data?.options.statuts.map((statut) => (
            <option key={statut.statut} value={statut.statut}>
              {statut.libelle} ({statut.dossiers})
            </option>
          ))}
        </ChampFiltre>
        <ChampFiltre valeur={filtres.situation} onChange={(valeur) => changer('situation', valeur)} etiquette="Situation">
          <option value="">En formation ou non</option>
          <option value="EN_FORMATION">En formation</option>
          <option value="HORS_FORMATION">Hors formation</option>
        </ChampFiltre>
        <ChampFiltre valeur={filtres.contrat} onChange={(valeur) => changer('contrat', valeur)} etiquette="Contrat">
          <option value="">Tous les contrats</option>
          {ETATS_CONTRAT.map((etat) => (
            <option key={etat.valeur} value={etat.valeur}>
              {etat.libelle}
            </option>
          ))}
        </ChampFiltre>
        <ChampFiltre valeur={filtres.referent} onChange={(valeur) => changer('referent', valeur)} etiquette="Formateur référent">
          <option value="">Tous les référents</option>
          {data?.options.referents.map((referent) => (
            <option key={referent.id} value={referent.id}>
              {referent.nom}
            </option>
          ))}
          <option value="SANS">Sans formateur référent</option>
        </ChampFiltre>
        <ChampFiltre valeur={filtres.mode} onChange={(valeur) => changer('mode', valeur)} etiquette="Mode du dossier">
          <option value="">CFA et OF</option>
          <option value="CFA">CFA · apprentissage</option>
          <option value="OF">OF · session de formation</option>
        </ChampFiltre>
        <FiltreBascule libelle="Mineur" actif={filtres.mineur} onBasculer={() => changer('mineur', !filtres.mineur)} />
        <FiltreBascule libelle="RQTH" actif={filtres.rqth} onBasculer={() => changer('rqth', !filtres.rqth)} />
        <FiltreBascule libelle="Seuil d’absences atteint" actif={filtres.seuil} onBasculer={() => changer('seuil', !filtres.seuil)} />
      </BarreFiltres>

      <EtatChargement
        isLoading={isLoading}
        error={error}
        vide={!isLoading && !error && total === 0}
        libelle="Chargement des apprenants…"
        messageVide={compteurs?.dossiers ? 'Aucun apprenant ne correspond aux filtres.' : 'Aucun dossier dans ce périmètre.'}
      />

      {!isLoading && !error && total > 0 && (
        <div className="overflow-hidden rounded-2xl bg-white shadow-card">
          {/* Sous l'ordinateur, une carte par apprenant : le tableau y défilerait de côté. */}
          <ul className="grid grid-cols-1 divide-y divide-gray-100 md:grid-cols-2 md:divide-y-0 xl:hidden">
            {lignes.map((ligne) => (
              <li key={ligne.id} className="space-y-3 border-gray-100 p-4 md:border-b md:odd:border-r">
                <IdentiteApprenant ligne={ligne} />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <FormationApprenant ligne={ligne} />
                  <StatutApprenant ligne={ligne} />
                  <EntrepriseApprenant ligne={ligne} />
                  <SuiviApprenant ligne={ligne} />
                </div>
                <PresenceApprenant ligne={ligne} />
                <Link to={`/adm-dossier/${ligne.id}`} className={CLASSE_LIEN_FICHE}>
                  Ouvrir le dossier
                </Link>
              </li>
            ))}
          </ul>
          <div className="relative hidden overflow-x-auto xl:block">
            <table className="w-full min-w-[960px] text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {['Apprenant', 'Formation', 'Statut · parcours', 'Entreprise · financement', 'Suivi', 'Présence', ''].map((colonne, index) => (
                    <th key={colonne || index} className={CLASSE_TH}>
                      {colonne || <span className="sr-only">Actions</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lignes.map((ligne) => (
                  <tr key={ligne.id} className="transition hover:bg-gray-50">
                    <td className={`${CLASSE_TD} max-w-[220px]`}>
                      <IdentiteApprenant ligne={ligne} />
                    </td>
                    <td className={`${CLASSE_TD} max-w-[200px]`}>
                      <FormationApprenant ligne={ligne} />
                    </td>
                    <td className={CLASSE_TD}>
                      <StatutApprenant ligne={ligne} />
                    </td>
                    <td className={`${CLASSE_TD} max-w-[220px]`}>
                      <EntrepriseApprenant ligne={ligne} />
                    </td>
                    <td className={`${CLASSE_TD} max-w-[180px]`}>
                      <SuiviApprenant ligne={ligne} />
                    </td>
                    <td className={CLASSE_TD}>
                      <PresenceApprenant ligne={ligne} />
                    </td>
                    <td className={`${CLASSE_TD} text-right`}>
                      <Link to={`/adm-dossier/${ligne.id}`} className={CLASSE_LIEN_FICHE}>
                        Ouvrir le dossier
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pages={Math.max(1, Math.ceil(total / taillePage))}
            total={total}
            affiches={lignes.length}
            taillePage={taillePage}
            onPage={setPage}
            onChangerTaille={setTaillePage}
            uniteLabel="apprenant"
          />
        </div>
      )}

      {exportOuvert && (
        <ModaleExportApprenants
          total={total}
          filtres={parametres}
          onFermer={() => setExportOuvert(false)}
        />
      )}
    </div>
  );
}

function DateParcours({ libelle, date }: { libelle: string; date: DateLue | null }): JSX.Element {
  return (
    <span title={date?.provenance ?? undefined}>
      {libelle} {date ? jourCourt(date.jour) : 'à fixer'}
      {date?.provenance ? '*' : ''}
    </span>
  );
}

function IdentiteApprenant({ ligne }: { ligne: LigneApprenantGlobale }): JSX.Element {
  return (
    <div className="flex min-w-0 items-start gap-3">
      <Vignette initiales={ligne.initiales} />
      <div className="min-w-0">
        <Link
          to={`/adm-dossier/${ligne.id}`}
          className="block truncate font-semibold text-codialis-navy hover:text-codialis-sky hover:underline [@media(pointer:coarse)]:leading-10"
        >
          {ligne.prenom} {ligne.nom}
        </Link>
        <div className="flex flex-wrap items-center gap-1 pt-0.5">
          <PastilleSite site={ligne.siteCode} />
          {ligne.mineur && <Pastille texte="Mineur" ton="warning" />}
          {ligne.rqth && <Pastille texte="RQTH" ton="info" />}
        </div>
        <Detail>{ligne.age === null ? 'Âge inconnu' : `${ligne.age} ans`}</Detail>
      </div>
    </div>
  );
}

function FormationApprenant({ ligne }: { ligne: LigneApprenantGlobale }): JSX.Element {
  return (
    <div className="min-w-0 text-xs">
      <div className="font-medium text-codialis-navy">{ligne.formation?.titre ?? 'Sans formation rattachée'}</div>
      <Detail>
        {ligne.mode} · {ligne.promotion.libelle}
      </Detail>
    </div>
  );
}

function StatutApprenant({ ligne }: { ligne: LigneApprenantGlobale }): JSX.Element {
  return (
    <div className="min-w-0 space-y-1 text-xs">
      <div className="flex flex-wrap items-center gap-1">
        <Pastille texte={ligne.statutLibelle} ton={ligne.statutTon} />
        {/* Sous contrat, maintenu après une rupture, complet et entré : en formation sans que le statut le dise. */}
        {ligne.enFormation && ligne.statut !== 'EN_FORMATION' && <Pastille texte="En formation" ton="mint" />}
      </div>
      <Detail>
        <DateParcours libelle="Début" date={ligne.debut} /> · <DateParcours libelle="fin" date={ligne.fin} />
      </Detail>
      <Detail>
        <DateParcours libelle="Examen" date={ligne.examen} />
      </Detail>
    </div>
  );
}

function EntrepriseApprenant({ ligne }: { ligne: LigneApprenantGlobale }): JSX.Element {
  return (
    <div className="min-w-0 space-y-1 text-xs">
      {ligne.contrat ? (
        <>
          <div className="flex flex-wrap items-center gap-1">
            <span className="font-medium text-codialis-navy">{ligne.contrat.entreprise}</span>
            <Pastille texte={ligne.contrat.libelle} ton={ligne.contrat.ton} />
          </div>
          {ligne.contrat.maitreApprentissage && <Detail>Maître d’apprentissage : {ligne.contrat.maitreApprentissage}</Detail>}
        </>
      ) : (
        <Detail>Sans contrat</Detail>
      )}
      {ligne.financement && (
        <div title={ligne.financement.detail ?? undefined}>
          <Pastille texte={ligne.financement.libelle} ton={ligne.financement.ton} />
        </div>
      )}
    </div>
  );
}

function SuiviApprenant({ ligne }: { ligne: LigneApprenantGlobale }): JSX.Element {
  return (
    <div className="min-w-0 space-y-0.5 text-xs">
      <div className="text-codialis-navy">
        <span className="text-codialis-gray">Référent : </span>
        {ligne.referent?.nom ?? '—'}
      </div>
      <div className="text-codialis-navy">
        <span className="text-codialis-gray">Conseiller : </span>
        {ligne.conseiller ?? '—'}
      </div>
      <Detail>
        Pièces : {ligne.pieces.deposees}/{ligne.pieces.attendues}
      </Detail>
    </div>
  );
}

function PresenceApprenant({ ligne }: { ligne: LigneApprenantGlobale }): JSX.Element {
  const { assiduite } = ligne;
  // Un taux de 0 % se lirait comme une absence totale : sans émargement, on le dit.
  if (assiduite.emargees === 0) return <Detail>Aucune demi-journée émargée</Detail>;
  return (
    <div className="min-w-0 text-xs">
      <span className="font-semibold text-codialis-navy">{taux(assiduite.tauxPresence)}</span>
      <span className="text-codialis-gray"> de présence</span>
      <div className={assiduite.seuilAtteint ? 'text-[11px] font-semibold text-red-600' : 'text-[11px] text-codialis-gray'}>
        {assiduite.absencesNonJustifiees}/{assiduite.seuil} absence{assiduite.absencesNonJustifiees > 1 ? 's' : ''} non justifiée
        {assiduite.absencesNonJustifiees > 1 ? 's' : ''}
      </div>
      <Detail>{pluriel(assiduite.emargees, 'demi-journée émargée', 'demi-journées émargées')}</Detail>
    </div>
  );
}

/**
 * Export des apprenants : toutes les colonnes de la liste (décision du
 * 21/10/2026). Mineur, RQTH et représentant légal ne sortent que cochés, et
 * l'API trace l'export dans le journal de chaque dossier.
 */
function ModaleExportApprenants({
  total,
  filtres,
  onFermer,
}: {
  total: number;
  filtres: Filtres;
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const [sensibles, setSensibles] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const exporter = async (): Promise<void> => {
    setEnCours(true);
    setErreur(null);
    try {
      const { lignes } = await exporterVueGlobale('apprenants', filtres, { sensibles });
      toast(`Fichier exporté — ${pluriel(lignes, 'apprenant')}`);
      onFermer();
    } catch (refus) {
      setErreur(messageErreur(refus, 'L’export n’a pas pu être produit.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale ouverte titre="Exporter les apprenants" sousTitre="La liste filtrée, toutes pages comprises" onFermer={onFermer} largeur="max-w-lg">
      <div className="space-y-4 p-4 sm:p-6">
        <p className="text-sm text-codialis-navy">
          <span className="font-semibold">{pluriel(total, 'apprenant')}</span> dans le classeur Excel, avec toutes les
          colonnes de la liste : identité et coordonnées, formation, statut et dates, entreprise et contrat, référents,
          financement, présence et pièces.
        </p>
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 p-3">
          <input
            type="checkbox"
            checked={sensibles}
            onChange={(evenement) => setSensibles(evenement.target.checked)}
            className="mt-0.5 h-5 w-5 shrink-0 accent-codialis-navy"
          />
          <span className="min-w-0 text-sm">
            <span className="font-semibold text-codialis-navy">Inclure les données sensibles</span>
            <span className="block text-xs text-codialis-gray">
              Minorité, RQTH (donnée de santé), nom, e-mail et adresse du représentant légal.
            </span>
          </span>
        </label>
        <p className="flex items-start gap-2 rounded-xl bg-codialis-sand p-3 text-xs text-codialis-gray">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-codialis-navy" aria-hidden />
          L’export est tracé dans le journal d’accès de chacun des dossiers exportés
          {sensibles ? ', avec la mention des données sensibles' : ''}.
        </p>
        {erreur && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <BoutonSecondaire onClick={onFermer} disabled={enCours}>
            Annuler
          </BoutonSecondaire>
          <BoutonPrincipal onClick={() => void exporter()} disabled={enCours || total === 0}>
            <Download className="mr-1.5 h-4 w-4" aria-hidden />
            {enCours ? 'Export…' : 'Exporter'}
          </BoutonPrincipal>
        </div>
      </div>
    </Modale>
  );
}

// ---------------------------------------------------------------------------
// Formateurs
// ---------------------------------------------------------------------------

interface FiltresFormateurs extends FiltrePeriode {
  recherche: string;
  statut: string;
  actif: string;
  aSuivre: boolean;
}

const SANS_FILTRE_FORMATEURS: FiltresFormateurs = { ...PERIODE_PAR_DEFAUT, recherche: '', statut: '', actif: '', aSuivre: false };

function VueFormateurs({ site }: { site: string }): JSX.Element {
  const [filtres, setFiltres] = useState<FiltresFormateurs>(SANS_FILTRE_FORMATEURS);
  const recherche = useValeurDifferee(filtres.recherche.trim(), 300);
  const parametres: Filtres = {
    site,
    periode: filtres.periode,
    du: filtres.periode === 'LIBRE' ? filtres.du : undefined,
    au: filtres.periode === 'LIBRE' ? filtres.au : undefined,
    recherche,
    statut: filtres.statut,
    actif: filtres.actif,
    aSuivre: filtres.aSuivre,
  };
  const { data, isLoading, error } = useFormateursGlobale(parametres);
  const { enCours, exporter } = useExportSimple('formateurs', parametres);
  const filtreActif = JSON.stringify(filtres) !== JSON.stringify(SANS_FILTRE_FORMATEURS);
  const lignes = data?.formateurs ?? [];
  const compteurs = data?.compteurs;
  const periode = data?.periode.libelle ?? 'la période';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Compteur
          libelle="Formateurs"
          valeur={compteurs?.formateurs ?? '—'}
          detail={compteurs?.sansFiche ? `+ ${pluriel(compteurs.sansFiche, 'compte')} sans fiche` : 'Avec leur fiche formateur'}
        />
        <Compteur libelle="Heures animées" valeur={compteurs ? dureeHeures(compteurs.minutes) : '—'} ton="mint" detail={`Feuilles clôturées · ${periode}`} />
        <Compteur
          libelle="Fiches hors délai"
          valeur={compteurs?.fichesHorsDelai ?? '—'}
          ton={compteurs && compteurs.fichesHorsDelai > 0 ? 'red' : 'navy'}
          detail="Fiches séquence au-delà de huit jours"
        />
        <Compteur
          libelle="Heures à déclarer"
          valeur={compteurs?.declarationsEnAttente ?? '—'}
          ton={compteurs && compteurs.declarationsEnAttente > 0 ? 'warm' : 'navy'}
          detail="Formateurs dont un mois manque"
        />
      </div>

      <BarreFiltres
        onReinitialiser={filtreActif ? () => setFiltres(SANS_FILTRE_FORMATEURS) : null}
        onExporter={() => void exporter()}
        exportEnCours={enCours}
      >
        <ChampRecherche
          valeur={filtres.recherche}
          onChange={(valeur) => setFiltres((avant) => ({ ...avant, recherche: valeur }))}
          etiquette="Rechercher un formateur"
          placeholder="Nom, prénom ou e-mail…"
        />
        <ChoixDePeriode
          filtre={filtres}
          options={data?.filtres.periodes}
          affichee={data?.periode}
          onChanger={(periode) => setFiltres((avant) => ({ ...avant, ...periode }))}
        />
        <ChampFiltre valeur={filtres.statut} onChange={(valeur) => setFiltres((avant) => ({ ...avant, statut: valeur }))} etiquette="Statut">
          <option value="">Tous les statuts</option>
          <option value="INDEPENDANT">Indépendants</option>
          <option value="SALARIE">Salariés</option>
          <option value="SANS_FICHE">Sans fiche formateur</option>
        </ChampFiltre>
        <ChampFiltre valeur={filtres.actif} onChange={(valeur) => setFiltres((avant) => ({ ...avant, actif: valeur }))} etiquette="Compte">
          <option value="">Comptes actifs et inactifs</option>
          <option value="true">Comptes actifs</option>
          <option value="false">Comptes inactifs</option>
        </ChampFiltre>
        <FiltreBascule libelle="À suivre" actif={filtres.aSuivre} onBasculer={() => setFiltres((avant) => ({ ...avant, aSuivre: !avant.aSuivre }))} />
      </BarreFiltres>

      {filtres.periode === 'LIBRE' && filtres.du && filtres.au && filtres.du > filtres.au && (
        <MessageErreur message="Le premier jour de la période doit précéder le dernier." />
      )}
      <EtatChargement
        isLoading={isLoading}
        error={error}
        vide={!isLoading && !error && lignes.length === 0}
        libelle="Chargement des formateurs…"
        messageVide={compteurs && compteurs.formateurs + compteurs.sansFiche > 0 ? 'Aucun formateur ne correspond aux filtres.' : 'Aucun formateur dans ce périmètre.'}
      />

      {!isLoading && !error && lignes.length > 0 && (
        <div className="overflow-hidden rounded-2xl bg-white shadow-card">
          <ul className="grid grid-cols-1 divide-y divide-gray-100 md:grid-cols-2 md:divide-y-0 xl:hidden">
            {lignes.map((ligne) => (
              <li key={ligne.id} className="space-y-3 border-gray-100 p-4 md:border-b md:odd:border-r">
                <IdentiteFormateur ligne={ligne} />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <ApprenantsFormateur ligne={ligne} />
                  <ActiviteFormateur ligne={ligne} />
                  <FichesFormateur ligne={ligne} />
                  <PiecesFormateur ligne={ligne} />
                </div>
                <LienFicheFormateur ligne={ligne} />
              </li>
            ))}
          </ul>
          <div className="relative hidden overflow-x-auto xl:block">
            <table className="w-full min-w-[960px] text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {['Formateur', 'Apprenants', `Activité · ${periode}`, 'Fiches · heures', 'Pièces · factures', ''].map((colonne, index) => (
                    <th key={colonne || index} className={CLASSE_TH}>
                      {colonne || <span className="sr-only">Actions</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lignes.map((ligne) => (
                  <tr key={ligne.id} className="transition hover:bg-gray-50">
                    <td className={`${CLASSE_TD} max-w-[260px]`}>
                      <IdentiteFormateur ligne={ligne} />
                    </td>
                    <td className={CLASSE_TD}>
                      <ApprenantsFormateur ligne={ligne} />
                    </td>
                    <td className={CLASSE_TD}>
                      <ActiviteFormateur ligne={ligne} />
                    </td>
                    <td className={CLASSE_TD}>
                      <FichesFormateur ligne={ligne} />
                    </td>
                    <td className={CLASSE_TD}>
                      <PiecesFormateur ligne={ligne} />
                    </td>
                    <td className={`${CLASSE_TD} text-right`}>
                      <LienFicheFormateur ligne={ligne} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="border-t border-gray-100 bg-gray-50 px-4 py-3 text-xs text-codialis-gray sm:px-5">{pluriel(lignes.length, 'formateur')}</p>
        </div>
      )}
    </div>
  );
}

function IdentiteFormateur({ ligne }: { ligne: LigneFormateurGlobale }): JSX.Element {
  return (
    <div className="flex min-w-0 items-start gap-3">
      <Vignette initiales={ligne.initiales} />
      <div className="min-w-0">
        <div className="truncate font-semibold text-codialis-navy">
          {ligne.prenom} {ligne.nom}
        </div>
        <Detail>{ligne.statutLibelle}</Detail>
        <div className="flex flex-wrap items-center gap-1 pt-0.5">
          {ligne.sites.map((code) => (
            <PastilleSite key={code} site={code} />
          ))}
          {!ligne.actif && <Pastille texte="Compte inactif" ton="neutral" />}
        </div>
        {ligne.tauxHoraire && <Detail>{euros(ligne.tauxHoraire.centimes / 100)} HT de l’heure</Detail>}
      </div>
    </div>
  );
}

function ApprenantsFormateur({ ligne }: { ligne: LigneFormateurGlobale }): JSX.Element {
  return (
    <div className="min-w-0 text-xs">
      <span className="font-semibold text-codialis-navy">{pluriel(ligne.apprenants.referents, 'apprenant référent', 'apprenants référents')}</span>
      <Detail>dont {ligne.apprenants.enFormation} en formation</Detail>
    </div>
  );
}

function ActiviteFormateur({ ligne }: { ligne: LigneFormateurGlobale }): JSX.Element {
  return (
    <div className="min-w-0 text-xs">
      <span className="font-semibold text-codialis-navy">{dureeHeures(ligne.activite.minutes)}</span>
      <span className="text-codialis-gray"> animées</span>
      <Detail>{pluriel(ligne.activite.demiJournees, 'demi-journée clôturée', 'demi-journées clôturées')}</Detail>
      {ligne.activite.feuillesOuvertes > 0 && (
        <div className="text-[11px] font-semibold text-amber-700">
          {pluriel(ligne.activite.feuillesOuvertes, 'feuille à clôturer', 'feuilles à clôturer')}
        </div>
      )}
    </div>
  );
}

function FichesFormateur({ ligne }: { ligne: LigneFormateurGlobale }): JSX.Element {
  const { fiches, declarations } = ligne;
  const rien = fiches.horsDelai === 0 && fiches.aValider === 0 && declarations.enAttente === 0;
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1 text-xs">
      {fiches.horsDelai > 0 && <Pastille texte={`${pluriel(fiches.horsDelai, 'fiche')} hors délai`} ton="danger" />}
      {fiches.aValider > 0 && <Pastille texte={`${pluriel(fiches.aValider, 'fiche')} à valider`} ton="warning" />}
      {declarations.enAttente > 0 && (
        <span title={declarations.mois.join(', ')}>
          <Pastille texte={`Heures à déclarer : ${declarations.mois.join(', ')}`} ton="warning" />
        </span>
      )}
      {rien && <span className="text-codialis-gray">Rien en attente</span>}
    </div>
  );
}

function PiecesFormateur({ ligne }: { ligne: LigneFormateurGlobale }): JSX.Element {
  if (ligne.formateurId === null) return <Detail>Fiche formateur à créer</Detail>;
  const rien = ligne.pieces.aVerifier + ligne.pieces.nonConformes + ligne.facturesAVerifier === 0;
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1 text-xs">
      {ligne.pieces.nonConformes > 0 && (
        <Pastille texte={`${pluriel(ligne.pieces.nonConformes, 'pièce')} non conforme${ligne.pieces.nonConformes > 1 ? 's' : ''}`} ton="danger" />
      )}
      {ligne.pieces.aVerifier > 0 && <Pastille texte={`${pluriel(ligne.pieces.aVerifier, 'pièce')} à vérifier`} ton="warning" />}
      {ligne.facturesAVerifier > 0 && <Pastille texte={`${pluriel(ligne.facturesAVerifier, 'facture')} à vérifier`} ton="warning" />}
      {rien && <span className="text-codialis-gray">Pièces à jour</span>}
    </div>
  );
}

function LienFicheFormateur({ ligne }: { ligne: LigneFormateurGlobale }): JSX.Element {
  return (
    <Link to={ligne.formateurId ? `/instructors?formateur=${ligne.formateurId}` : '/instructors'} className={CLASSE_LIEN_FICHE}>
      {ligne.formateurId ? 'Ouvrir la fiche' : 'Créer la fiche'}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Formations
// ---------------------------------------------------------------------------

interface FiltresFormations extends FiltrePeriode {
  recherche: string;
  etat: string;
  niveau: string;
}

const SANS_FILTRE_FORMATIONS: FiltresFormations = { ...PERIODE_PAR_DEFAUT, recherche: '', etat: '', niveau: '' };

function VueFormations({ site }: { site: string }): JSX.Element {
  const [filtres, setFiltres] = useState<FiltresFormations>(SANS_FILTRE_FORMATIONS);
  const recherche = useValeurDifferee(filtres.recherche.trim(), 300);
  const parametres: Filtres = {
    site,
    periode: filtres.periode,
    du: filtres.periode === 'LIBRE' ? filtres.du : undefined,
    au: filtres.periode === 'LIBRE' ? filtres.au : undefined,
    recherche,
    etat: filtres.etat,
    niveau: filtres.niveau,
  };
  const { data, isLoading, error } = useFormationsGlobale(parametres);
  const { enCours, exporter } = useExportSimple('formations', parametres);
  const filtreActif = JSON.stringify(filtres) !== JSON.stringify(SANS_FILTRE_FORMATIONS);
  const lignes = data?.formations ?? [];
  const compteurs = data?.compteurs;
  const periode = data?.periode.libelle ?? 'la période';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Compteur libelle="Formations actives" valeur={compteurs?.actives ?? '—'} detail="Au catalogue" />
        <Compteur libelle="Apprenants en formation" valeur={compteurs?.enFormation ?? '—'} ton="mint" detail="Toutes formations, aujourd’hui" />
        <Compteur libelle="Sessions en cours" valeur={compteurs?.sessionsEnCours ?? '—'} ton="blue" detail="Sessions OF de l’antenne" />
        <Compteur libelle="Heures formées" valeur={compteurs ? dureeHeures(compteurs.minutes) : '—'} ton="mint" detail={`Feuilles clôturées · ${periode}`} />
      </div>

      <BarreFiltres
        onReinitialiser={filtreActif ? () => setFiltres(SANS_FILTRE_FORMATIONS) : null}
        onExporter={() => void exporter()}
        exportEnCours={enCours}
      >
        <ChampRecherche
          valeur={filtres.recherche}
          onChange={(valeur) => setFiltres((avant) => ({ ...avant, recherche: valeur }))}
          etiquette="Rechercher une formation"
          placeholder="Code, intitulé, RNCP…"
        />
        <ChoixDePeriode
          filtre={filtres}
          options={data?.filtres.periodes}
          affichee={data?.periode}
          onChanger={(periode) => setFiltres((avant) => ({ ...avant, ...periode }))}
        />
        <ChampFiltre valeur={filtres.etat} onChange={(valeur) => setFiltres((avant) => ({ ...avant, etat: valeur }))} etiquette="État">
          <option value="">Actives et inactives</option>
          <option value="ACTIVES">Actives</option>
          <option value="INACTIVES">Inactives</option>
        </ChampFiltre>
        <ChampFiltre valeur={filtres.niveau} onChange={(valeur) => setFiltres((avant) => ({ ...avant, niveau: valeur }))} etiquette="Niveau">
          <option value="">Tous les niveaux</option>
          {data?.filtres.niveaux.map((niveau) => (
            <option key={niveau} value={niveau}>
              {niveau}
            </option>
          ))}
        </ChampFiltre>
      </BarreFiltres>

      {filtres.periode === 'LIBRE' && filtres.du && filtres.au && filtres.du > filtres.au && (
        <MessageErreur message="Le premier jour de la période doit précéder le dernier." />
      )}
      <EtatChargement
        isLoading={isLoading}
        error={error}
        vide={!isLoading && !error && lignes.length === 0}
        libelle="Chargement des formations…"
        messageVide="Aucune formation ne correspond aux filtres."
      />

      {!isLoading && !error && lignes.length > 0 && (
        <div className="overflow-hidden rounded-2xl bg-white shadow-card">
          <ul className="grid grid-cols-1 divide-y divide-gray-100 md:grid-cols-2 md:divide-y-0 xl:hidden">
            {lignes.map((ligne) => (
              <li key={ligne.id} className="space-y-3 border-gray-100 p-4 md:border-b md:odd:border-r">
                <IdentiteFormation ligne={ligne} />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <EffectifsFormation ligne={ligne} />
                  <SessionsFormation ligne={ligne} />
                  <ActiviteFormation ligne={ligne} />
                </div>
                <Link to={`/adm-formations?formation=${ligne.id}`} className={CLASSE_LIEN_FICHE}>
                  Ouvrir le programme
                </Link>
              </li>
            ))}
          </ul>
          <div className="relative hidden overflow-x-auto xl:block">
            <table className="w-full min-w-[960px] text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {['Formation', 'Effectifs', 'Promotions · sessions', `Activité · ${periode}`, ''].map((colonne, index) => (
                    <th key={colonne || index} className={CLASSE_TH}>
                      {colonne || <span className="sr-only">Actions</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lignes.map((ligne) => (
                  <tr key={ligne.id} className={`transition hover:bg-gray-50 ${ligne.actif ? '' : 'bg-gray-50/40'}`}>
                    <td className={`${CLASSE_TD} max-w-[320px]`}>
                      <IdentiteFormation ligne={ligne} />
                    </td>
                    <td className={CLASSE_TD}>
                      <EffectifsFormation ligne={ligne} />
                    </td>
                    <td className={CLASSE_TD}>
                      <SessionsFormation ligne={ligne} />
                    </td>
                    <td className={CLASSE_TD}>
                      <ActiviteFormation ligne={ligne} />
                    </td>
                    <td className={`${CLASSE_TD} text-right`}>
                      <Link to={`/adm-formations?formation=${ligne.id}`} className={CLASSE_LIEN_FICHE}>
                        Ouvrir le programme
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="border-t border-gray-100 bg-gray-50 px-4 py-3 text-xs text-codialis-gray sm:px-5">{pluriel(lignes.length, 'formation')}</p>
        </div>
      )}
    </div>
  );
}

function IdentiteFormation({ ligne }: { ligne: LigneFormationGlobale }): JSX.Element {
  return (
    <div className="min-w-0 space-y-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="rounded-md bg-codialis-navy/10 px-2 py-0.5 font-mono text-xs font-bold text-codialis-navy">{ligne.code}</span>
        {!ligne.actif && <Pastille texte="Inactive" ton="neutral" />}
        {ligne.antennes.map((code) => (
          <PastilleSite key={code} site={code} />
        ))}
      </div>
      <div className="font-semibold leading-snug text-codialis-navy">{ligne.titre}</div>
      <Detail>
        {ligne.niveau} · RNCP {ligne.rncp} · {heures(ligne.heures)} au programme
      </Detail>
    </div>
  );
}

function EffectifsFormation({ ligne }: { ligne: LigneFormationGlobale }): JSX.Element {
  const { effectifs } = ligne;
  return (
    <div className="min-w-0 text-xs">
      <span className="font-semibold text-codialis-navy">{effectifs.enFormation} en formation</span>
      <Detail>
        {pluriel(effectifs.dossiers, 'dossier')} · {pluriel(effectifs.termines, 'terminé')} · {pluriel(effectifs.ruptures, 'rupture')}
      </Detail>
    </div>
  );
}

function SessionsFormation({ ligne }: { ligne: LigneFormationGlobale }): JSX.Element {
  return (
    <div className="min-w-0 text-xs">
      <span className="font-semibold text-codialis-navy">{pluriel(ligne.promotions, 'promotion')}</span>
      <Detail>
        {ligne.sessions.enCours} session{ligne.sessions.enCours > 1 ? 's' : ''} en cours · {ligne.sessions.aVenir} à venir
      </Detail>
    </div>
  );
}

function ActiviteFormation({ ligne }: { ligne: LigneFormationGlobale }): JSX.Element {
  return (
    <div className="min-w-0 text-xs">
      <span className="font-semibold text-codialis-navy">{dureeHeures(ligne.activite.minutes)}</span>
      <span className="text-codialis-gray"> formées</span>
      <Detail>
        {pluriel(ligne.activite.demiJournees, 'demi-journée')}
        {ligne.activite.tauxPresence !== null && ` · présence ${taux(ligne.activite.tauxPresence)}`}
      </Detail>
    </div>
  );
}
